<template>
  <div class="standard-page v-flex">
    <b-button-toolbar
      v-if="activeSection === 'list'"
      class="button-toolbar"
      aria-label="Groups toolbar"
    >
      <b-input-group id="search-group">
        <b-form-input
          id="search-input"
          v-model="search"
          placeholder="Search..."
          type="text"
          data-cy="groups-search"
        />
        <b-input-group-text class="search-icon">
          <Search />
        </b-input-group-text>
      </b-input-group>

      <b-button
        id="create-group-button"
        variant="blue"
        data-cy="create-group"
        @click="createGroup"
      >
        Create Group
      </b-button>
    </b-button-toolbar>
    <GroupsList
      v-if="activeSection === 'list'"
      :search="search"
      :open-group-pkid="openGroupPkid"
      data-cy="groups-list-section"
      @edit="openEditor"
    />
    <GroupForm
      v-else
      ref="form"
      :key="editingGroup ? editingGroup.pkid : 'new'"
      :group="editingGroup"
      :template-id="presetTemplateId"
      data-cy="groups-edit-section"
      @cancel="showList"
      @saved="leaveSaved"
      @deleted="leaveSaved"
    />
  </div>
</template>

<script lang="ts">
import { Search } from 'lucide-vue'
import VueRouter from 'vue-router'
import { Component, Mixins, Watch } from 'vue-property-decorator'
import { DiscardDraftConfirmation } from '@/mixins/ModalMixins/ModalMixins'

const { isNavigationFailure, NavigationFailureType } = VueRouter
import GroupForm from './GroupForm.vue'
import GroupsList from './GroupsList.vue'
import { GroupsPage, pageFromQuery, queryFromPage } from './groupsRoute'

Component.registerHooks(['beforeRouteUpdate', 'beforeRouteLeave'])

@Component({ components: { GroupForm, GroupsList, Search } })
export default class GroupsView extends Mixins(DiscardDraftConfirmation) {
  activeSection: 'list' | 'edit' = 'list'
  search = ''
  /** The group being edited; null means "create". Read by the editor once it exists. */
  editingGroup: { pkid: number; name?: string } | null = null
  /** Set by a save or delete, so leaving the editor then does not ask about unsaved changes. */
  leaveWithoutAsking = false
  /** From a template's "+ Add Automated Text": `?section=edit&template=<pkid>` opens a blank editor. */
  presetTemplateId: number | null = null
  // A `?group=<pkid>` the list has not opened yet: it opens the group once its rows load, then this clears.
  openGroupPkid: number | null = null

  mounted() {
    this.applyRoute()
  }

  // Back, Forward and links all land here: the query decides the section.
  @Watch('$route.query')
  applyRoute() {
    const page = pageFromQuery(this.$route.query)
    if (page.section === 'edit') {
      if (
        this.activeSection === 'edit' &&
        this.editingGroup?.pkid === page.groupPkid
      )
        return
      // The editor needs the group's row and automation, which the list loads.
      this.openGroupPkid = page.groupPkid
      this.activeSection = 'list'

      return
    }
    this.openGroupPkid = null
    if (page.section === 'create') {
      this.presetTemplateId = page.templatePkid
      this.editingGroup = null
      this.activeSection = 'edit'

      return
    }
    this.presetTemplateId = null
    this.activeSection = 'list'
  }

  openEditor(group: { pkid: number; name?: string } | null) {
    this.openGroupPkid = null
    this.editingGroup = group
    this.activeSection = 'edit'
    this.navigate(
      group
        ? { section: 'edit', groupPkid: group.pkid }
        : { section: 'create', templatePkid: this.presetTemplateId }
    )
  }

  createGroup() {
    this.presetTemplateId = null
    this.openEditor(null)
  }

  // The header breadcrumb, as Templates has it: "Groups ▸ Edit Group" leads back to the list.
  @Watch('activeSection', { immediate: true })
  @Watch('editingGroup')
  setNav() {
    const nav =
      this.activeSection === 'edit'
        ? [
            { crumbTitle: 'Groups', directTo: 'GroupList' },
            { crumbTitle: this.editingGroup ? 'Edit Group' : 'Create Group' }
          ]
        : [{ crumbTitle: 'Groups' }]
    this.$store.dispatch('setPageTitle', nav)
  }

  // The route decides the section (applyRoute), so a refused "leave without saving" keeps the editor up.
  showList() {
    this.navigate({ section: 'list' })
  }

  leaveSaved() {
    this.leaveWithoutAsking = true
    this.showList()
  }

  /** Leaving the editor with unsaved changes asks first, as the customer and template pages do. */
  async mayLeaveEditor(): Promise<boolean> {
    const form = this.$refs.form as { isDirty?: boolean } | undefined
    const ask =
      this.activeSection === 'edit' &&
      Boolean(form?.isDirty) &&
      !this.leaveWithoutAsking
    this.leaveWithoutAsking = false

    return ask ? Boolean(await this.discardDraftConfirmation()) : true
  }

  async beforeRouteUpdate(
    to: unknown,
    from: unknown,
    next: (go?: boolean) => void
  ) {
    next(await this.mayLeaveEditor())
  }

  async beforeRouteLeave(
    to: unknown,
    from: unknown,
    next: (go?: boolean) => void
  ) {
    next(await this.mayLeaveEditor())
  }

  /** A new history entry per section change, so Back returns to the one before; a repeat is not one. */
  navigate(page: GroupsPage) {
    const query = queryFromPage(page)
    if (JSON.stringify(query) === JSON.stringify(this.$route.query)) return
    // A refused "Leave Page?" aborts the push; that is the answer, not an error.
    this.$router.push({ query }).catch(error => {
      if (!isNavigationFailure(error, NavigationFailureType.aborted))
        throw error
    })
  }
}
</script>

<style lang="scss" scoped>
.search-icon svg {
  margin: 0 10px;
}

// Right-aligned like #create-template-button on the Templates page, same `blue` variant.
#create-group-button {
  margin: 0 5px 0 auto;
}
</style>
